import { FC, useMemo } from "react";
import { Box, BoxProps, useTheme } from "@smile2impress/components/relaunch";
import { sanitizeHtml, mergeDeepRight } from "@smile2impress/attraction-utils";
import { marked } from "marked";

// CMS rich-text fields store Markdown (Strapi markdown editor) with occasional
// embedded inline HTML (e.g. <u>). Convert Markdown to HTML, then sanitize.
const markdownToHtml = (markdown: string): string =>
    marked.parse(markdown, { gfm: true, breaks: true }) as string;

export interface RichTextProps extends Omit<BoxProps, "children"> {
    content: string;
}

const defaultStyles = {
    a: {
        color: "text.primary",
    },
    "ul, ol": {
        listStylePos: "inside",
    },
    "li > p": {
        display: "inline",
    },
    "h1,h2,h3,h4,h5,h6": {
        fontFamily: "heading",
        mb: 1,
    },
};

type Styles = { [x: string]: any };

export const RichText: FC<RichTextProps> = ({ content, sx, ...rest }) => {
    const { textStyles } = useTheme();

    const allStyles = useMemo(
        () =>
            mergeDeepRight(
                mergeDeepRight(
                    defaultStyles,
                    Array.from({ length: 6 }).reduce(
                        (acc: Styles, _val, index) => {
                            const style = textStyles?.[`H${index + 1}`];
                            return style
                                ? {
                                      ...acc,
                                      [`h${index + 1}`]:
                                          textStyles?.[`H${index + 1}`],
                                  }
                                : acc;
                        },
                        {} as Styles
                    ) as Styles
                ) ?? {},
                sx ?? {}
            ),
        [defaultStyles, textStyles, sx]
    );

    return (
        <Box
            sx={{ ...(allStyles as Styles) }}
            dangerouslySetInnerHTML={{
                __html: sanitizeHtml(markdownToHtml(content)),
            }}
            {...rest}
        />
    );
};
